import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import OurTeamCard from '../pages/OurTeamCard';

// ==================== LAYOUT COMPONENTS ====================
export const Section = ({ children, className = "", id }) => (
  <section id={id} className={`px-4 md:px-[8vw] py-20 md:py-28 ${className}`}>
    <div className="max-w-7xl mx-auto">{children}</div>
  </section>
);

export const SectionTitle = ({ children, subtitle, dark = false }) => (
  <div className="text-center mb-20 md:mb-24">
    <h2 className={`text-5xl md:text-6xl lg:text-7xl font-black mb-6 ${
      dark ? 'text-white' : 'text-slate-900'
    } tracking-tight leading-tight`}>
      {children}
    </h2>
    <div className="flex items-center justify-center gap-3 mb-8">
      <div className="h-1.5 w-24 bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 rounded-full shadow-lg shadow-orange-500/30" />
      <div className="w-2.5 h-2.5 bg-orange-500 rounded-full animate-pulse" />
      <div className="h-1.5 w-24 bg-gradient-to-r from-yellow-500 via-lime-500 to-green-500 rounded-full shadow-lg shadow-green-500/30" />
    </div>
    {subtitle && (
      <p className={`text-xl md:text-2xl max-w-4xl mx-auto leading-relaxed ${
        dark ? 'text-black' : 'text-slate-600'
      }`}>
        {subtitle}
      </p>
    )}
  </div>
);

// ==================== CARD COMPONENTS ====================
export const FeatureCard = ({ icon, text }) => (
  <div className="group relative rounded-3xl p-10 overflow-hidden bg-gradient-to-br from-slate-800 via-slate-900 to-black border border-slate-700/50 hover:border-orange-500/50 shadow-2xl hover:shadow-orange-500/20 transition-all duration-700 hover:-translate-y-3">
    {/* Efecto de luz de fondo */}
    <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 via-transparent to-green-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
    
    {/* Partículas brillantes */}
    <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-amber-500/20 to-transparent rounded-full blur-3xl group-hover:scale-150 transition-transform duration-1000" />
    <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-green-500/20 to-transparent rounded-full blur-2xl group-hover:scale-150 transition-transform duration-1000" />
    
    <div className="relative text-center">
      <div className="relative w-32 h-32 mx-auto mb-8">
        {/* Anillo exterior brillante */}
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500 via-amber-500 to-yellow-500 rounded-3xl blur-md opacity-60 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500" />
        
        {/* Círculo principal */}
        <div className="absolute inset-2 bg-gradient-to-br from-orange-600 via-amber-600 to-yellow-600 rounded-3xl shadow-2xl shadow-orange-500/50 group-hover:shadow-orange-500/80 transition-all duration-500" />
        
        {/* Brillo superior */}
        <div className="absolute inset-2 bg-gradient-to-br from-white/30 to-transparent rounded-3xl" />
        
        {/* Ícono */}
        <div className="absolute inset-0 flex items-center justify-center">
          <FontAwesomeIcon icon={icon} className="text-5xl text-black drop-shadow-2xl group-hover:scale-110 transition-transform duration-500" />
        </div>
      </div>
      
      <p className="text-black font-bold leading-relaxed text-lg">
        {text}
      </p>
    </div>
  </div>
);

export const ResearchCard = ({ icon, text }) => (
  <div className="group relative flex items-start gap-6 p-8 rounded-2xl bg-gradient-to-r from-slate-800 to-slate-900 border border-slate-700/50 hover:border-lime-500/50 hover:shadow-2xl hover:shadow-lime-500/20 transition-all duration-500">
    {/* Barra lateral con gradiente vibrante */}
    <div className="absolute left-0 top-0 bottom-0 w-2 bg-gradient-to-b from-orange-500 via-yellow-500 to-lime-500 rounded-l-2xl scale-y-0 group-hover:scale-y-100 transition-transform duration-500 origin-top shadow-lg shadow-lime-500/50" />
    
    {/* Resplandor de fondo */}
    <div className="absolute inset-0 bg-gradient-to-r from-orange-500/5 via-yellow-500/5 to-lime-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl" />
    
    <div className="relative flex-shrink-0">
      {/* Contenedor del ícono con gradiente vibrante */}
      <div className="relative w-20 h-20">
        {/* Resplandor */}
        <div className="absolute inset-0 bg-gradient-to-br from-lime-500 to-green-600 rounded-2xl blur-md opacity-60 group-hover:opacity-100 transition-opacity duration-500" />
        
        {/* Fondo sólido */}
        <div className="absolute inset-0 bg-gradient-to-br from-lime-500 via-green-500 to-emerald-600 rounded-2xl shadow-xl shadow-lime-500/30 group-hover:shadow-lime-500/60 group-hover:scale-110 transition-all duration-500 flex items-center justify-center">
          <FontAwesomeIcon icon={icon} className="text-3xl text-white relative z-10" />
        </div>
      </div>
    </div>
    
    <p className="relative text-black leading-relaxed pt-3 flex-1 font-medium">
      {text}
    </p>
  </div>
);

export const SponsorCard = ({ name, img }) => (
  <div className="group text-center">
    <div className="relative h-44 flex items-center justify-center mb-5 p-10 rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-slate-700/50 hover:border-amber-500/50 shadow-xl hover:shadow-2xl hover:shadow-amber-500/20 transition-all duration-500 overflow-hidden">
      {/* Efecto de resplandor */}
      <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      
     
      <img
        src={img}
        alt={name}
        className="relative max-h-full max-w-full object-contain brightness-110 contrast-110 group-hover:scale-110 transition-all duration-500 z-10 drop-shadow-2xl"
        loading="lazy"
      />
    </div>
    <p className="text-sm text-black font-medium">
      {name}
    </p>
  </div>
);

// ==================== CONTENT BLOCKS ====================
export const ContentBlock = ({ title, description, imageSrc, imageAlt, reverse, action }) => (
  <div className={`flex flex-col ${reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-16 lg:gap-24`}>
    <div className="lg:w-1/2 space-y-8">
      <h3 className="text-4xl md:text-5xl lg:text-6xl font-black leading-tight bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 bg-clip-text text-transparent drop-shadow-lg">
        {title}
      </h3>
      <div className="text-black text-xl leading-relaxed">{description}</div>
      {action && <div className="pt-6">{action}</div>}
    </div>
    
    <div className="lg:w-1/2">
      <div className="relative group">
        {/* Resplandor exterior masivo */}
        <div className="absolute -inset-8 bg-gradient-to-r from-orange-500/30 via-amber-500/30 to-yellow-500/30 rounded-[3rem] opacity-0 group-hover:opacity-100 blur-3xl transition-all duration-700" />
        
        {/* Borde brillante */}
        <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 via-amber-500 to-lime-500 rounded-[2.5rem] opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
        
        {/* Contenedor de imagen */}
        <div className="relative overflow-hidden rounded-[2.5rem] shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-600/20 via-transparent to-lime-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-700 z-10" />
          
          <img 
            src={imageSrc} 
            alt={imageAlt} 
            className="relative w-full rounded-[2.5rem] transform group-hover:scale-105 transition-transform duration-700 brightness-105" 
            loading="lazy" 
          />
        </div>
      </div>
    </div>
  </div>
);

// ==================== BUTTONS ====================
export const CTAButton = ({ to, children }) => (
  <a href={to} className="inline-block group">
    <button className="relative px-14 py-6 text-lg font-black text-[#000000] overflow-hidden rounded-2xl shadow-2xl shadow-orange-500/30 hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105">
      {/* Fondo con gradiente vibrante */}
      <div className="absolute inset-0 bg-gradient-to-r from-orange-600 via-amber-500 to-yellow-500" />
      
      {/* Capa de brillo animada */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
      
      {/* Borde interior brillante */}
      <div className="absolute inset-[2px] rounded-2xl bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500" />
      
      {/* Texto con ícono */}
      <span className="relative flex items-center justify-center gap-3 px-2">
        {children}
        <FontAwesomeIcon icon="fa-solid fa-arrow-right" className="group-hover:translate-x-2 transition-transform duration-300" />
      </span>
    </button>
  </a>
);

// ==================== TEAM CAROUSEL ====================
export const TeamCarousel = ({ members, t }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(4);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) setItemsPerView(1);
      else if (window.innerWidth < 768) setItemsPerView(2);
      else if (window.innerWidth < 1024) setItemsPerView(3);
      else setItemsPerView(4);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => 
        prev + itemsPerView >= members.length ? 0 : prev + 1
      );
    }, 4000);

    return () => clearInterval(interval);
  }, [members.length, itemsPerView]);

  const next = () => {
    setCurrentIndex((prev) => 
      prev + itemsPerView >= members.length ? 0 : prev + 1
    );
  };

  const prev = () => {
    setCurrentIndex((prev) => 
      prev === 0 ? Math.max(0, members.length - itemsPerView) : prev - 1
    );
  };

  const visibleMembers = members.slice(currentIndex, currentIndex + itemsPerView);
  
  if (visibleMembers.length < itemsPerView && currentIndex > 0) {
    const remaining = itemsPerView - visibleMembers.length;
    visibleMembers.push(...members.slice(0, remaining));
  }

  return (
    <div className="relative px-4">
      <div className="overflow-hidden">
        <div className="flex gap-8 transition-transform duration-700 ease-out">
          {visibleMembers.map((member, idx) => (
            <div key={idx} className="flex-shrink-0" style={{ width: `calc(${100 / itemsPerView}% - ${(itemsPerView - 1) * 32 / itemsPerView}px)` }}>
              <OurTeamCard member={member} translation={t.teamMembers?.[member.name]} />
            </div>
          ))}
        </div>
      </div>

      {members.length > itemsPerView && (
        <>
          <button
            onClick={prev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-6 w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl shadow-2xl shadow-orange-500/50 hover:shadow-orange-500/80 hover:scale-110 text-white transition-all duration-300 flex items-center justify-center z-10 group"
            aria-label="Anterior"
          >
            <FontAwesomeIcon icon="fa-solid fa-chevron-left" className="text-xl group-hover:-translate-x-1 transition-transform duration-300" />
          </button>
          
          <button
            onClick={next}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-6 w-16 h-16 bg-gradient-to-br from-lime-500 to-green-600 rounded-2xl shadow-2xl shadow-lime-500/50 hover:shadow-lime-500/80 hover:scale-110 text-white transition-all duration-300 flex items-center justify-center z-10 group"
            aria-label="Siguiente"
          >
            <FontAwesomeIcon icon="fa-solid fa-chevron-right" className="text-xl group-hover:translate-x-1 transition-transform duration-300" />
          </button>
        </>
      )}

      <div className="flex justify-center gap-3 mt-12">
        {Array.from({ length: Math.ceil(members.length / itemsPerView) }).map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentIndex(idx * itemsPerView)}
            className={`h-3 rounded-full transition-all duration-500 ${
              Math.floor(currentIndex / itemsPerView) === idx
                ? 'w-16 bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 shadow-lg shadow-orange-500/50'
                : 'w-3 bg-slate-600 hover:bg-amber-500 hover:w-8'
            }`}
            aria-label={`Ir a página ${idx + 1}`}
          />
        ))}
      </div>
    </div>
  );
};